import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Instagram, Youtube, Send, CheckCircle, Music2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import usePageVisibility from '../hooks/usePageVisibility';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Social Media URLs - These will be updated when user provides them
const SOCIAL_URLS = {
  youtube: 'https://www.youtube.com/channel/UCZ8ewM6AKFbobbU6TscphoA',
  facebook: 'https://www.facebook.com/profile.php?id=61579689947073',
  instagram: 'https://www.instagram.com/livingfruitministry/',
  tiktok: 'https://www.tiktok.com/@livingfruitministry',
  spotify: 'https://open.spotify.com/show/0uRoGxumk0eCNdLs0TJCDP',
};

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const { visibility } = usePageVisibility();

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setSubscribed(true);
      toast.success(t('footer.subscribed'));
      setEmail('');
    } catch (error) {
      if (error.response?.data?.detail?.includes('Already')) {
        toast.info('Already subscribed!');
      } else {
        toast.error(t('common.error'));
      }
    } finally {
      setIsSubscribing(false);
    }
  };
  
  return (
    <footer className="bg-charcoal text-white py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Newsletter Section */}
        <div className="bg-burgundy/20 rounded-2xl p-6 md:p-8 mb-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="font-serif text-xl text-white mb-2">{t('footer.newsletter')}</h3>
              <p className="text-white/70 text-sm max-w-md">{t('footer.newsletterDesc')}</p>
            </div>
            {subscribed ? (
              <div className="flex items-center gap-2 text-gold">
                <CheckCircle size={20} />
                <span>{t('footer.subscribed')}</span>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex gap-2 w-full md:w-auto">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('footer.emailPlaceholder')}
                  className="px-4 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-gold w-full md:w-64"
                  required
                  data-testid="newsletter-email"
                />
                <button
                  type="submit"
                  disabled={isSubscribing}
                  className="px-6 py-3 bg-gold text-charcoal rounded-full font-medium hover:bg-white transition-colors flex items-center gap-2"
                  data-testid="newsletter-submit"
                >
                  {isSubscribing ? '...' : <><Send size={16} /> {t('footer.subscribe')}</>}
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Logo & Mission */}
          <div className="md:col-span-1">
            <img 
              src="https://customer-assets.emergentagent.com/job_d52ae5dd-568a-4669-9c8f-951ff5c53e99/artifacts/g3qmizx9_IMG_2894.png" 
              alt="Living Fruit Ministry" 
              className="h-16 w-auto mb-4 brightness-0 invert"
            />
            <p className="text-white/70 font-sans leading-relaxed text-sm">
              {t('footer.mission')}
            </p>
            <div className="flex gap-3 mt-6">
              <a 
                href={SOCIAL_URLS.facebook || '#'} 
                target="_blank" 
                rel="noopener noreferrer"
                className={`p-2 bg-white/10 rounded-full hover:bg-[#1877F2] hover:text-white transition-colors ${!SOCIAL_URLS.facebook && 'opacity-40 cursor-not-allowed'}`}
                onClick={(e) => !SOCIAL_URLS.facebook && e.preventDefault()}
                data-testid="social-facebook"
                title={SOCIAL_URLS.facebook ? 'Follow on Facebook' : 'Coming soon'}
              >
                <Facebook size={18} />
              </a>
              <a 
                href={SOCIAL_URLS.instagram || '#'} 
                target="_blank" 
                rel="noopener noreferrer"
                className={`p-2 bg-white/10 rounded-full hover:bg-[#E4405F] hover:text-white transition-colors ${!SOCIAL_URLS.instagram && 'opacity-40 cursor-not-allowed'}`}
                onClick={(e) => !SOCIAL_URLS.instagram && e.preventDefault()}
                data-testid="social-instagram"
                title={SOCIAL_URLS.instagram ? 'Follow on Instagram' : 'Coming soon'}
              >
                <Instagram size={18} />
              </a>
              <a 
                href={SOCIAL_URLS.youtube} 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 bg-white/10 rounded-full hover:bg-[#FF0000] hover:text-white transition-colors" 
                data-testid="social-youtube"
                title="Subscribe on YouTube"
              >
                <Youtube size={18} />
              </a>
              <a 
                href={SOCIAL_URLS.tiktok || '#'} 
                target="_blank" 
                rel="noopener noreferrer"
                className={`p-2 bg-white/10 rounded-full hover:bg-black hover:text-white transition-colors ${!SOCIAL_URLS.tiktok && 'opacity-40 cursor-not-allowed'}`}
                onClick={(e) => !SOCIAL_URLS.tiktok && e.preventDefault()}
                data-testid="social-tiktok"
                title={SOCIAL_URLS.tiktok ? 'Follow on TikTok' : 'Coming soon'}
              >
                <Music2 size={18} />
              </a>
              <a 
                href={SOCIAL_URLS.spotify || '#'} 
                target="_blank" 
                rel="noopener noreferrer"
                className={`p-2 bg-white/10 rounded-full hover:bg-[#1DB954] hover:text-white transition-colors ${!SOCIAL_URLS.spotify && 'opacity-40 cursor-not-allowed'}`}
                onClick={(e) => !SOCIAL_URLS.spotify && e.preventDefault()}
                data-testid="social-spotify"
                title={SOCIAL_URLS.spotify ? 'Listen on Spotify' : 'Coming soon'}
              >
                <svg viewBox="0 0 24 24" className="w-[18px] h-[18px] fill-current">
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                </svg>
              </a>
            </div>
          </div>
          
          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-lg text-gold mb-4">{t('footer.quickLinks')}</h4>
            <div className="flex flex-col gap-2">
              {visibility.imNew !== false && <Link to="/im-new" className="text-white/70 hover:text-gold transition-colors text-sm" data-testid="footer-im-new">{t('nav.imNew')}</Link>}
              {visibility.about !== false && <Link to="/about" className="text-white/70 hover:text-gold transition-colors text-sm" data-testid="footer-about">{t('footer.aboutUs')}</Link>}
              {visibility.sermons !== false && <Link to="/sermons" className="text-white/70 hover:text-gold transition-colors text-sm" data-testid="footer-sermons">{t('nav.sermons')}</Link>}
              {visibility.events !== false && <Link to="/events" className="text-white/70 hover:text-gold transition-colors text-sm" data-testid="footer-events">{t('nav.events')}</Link>}
              {visibility.smallGroups !== false && <Link to="/small-groups" className="text-white/70 hover:text-gold transition-colors text-sm" data-testid="footer-small-groups">{t('nav.smallGroups')}</Link>}
              {visibility.blog !== false && <Link to="/blog" className="text-white/70 hover:text-gold transition-colors text-sm" data-testid="footer-blog">{t('nav.blog')}</Link>}
            </div>
          </div>

          {/* Get Involved */}
          <div>
            <h4 className="font-serif text-lg text-gold mb-4">Get Involved</h4>
            <div className="flex flex-col gap-2">
              {visibility.volunteer !== false && <Link to="/volunteer" className="text-white/70 hover:text-gold transition-colors text-sm" data-testid="footer-volunteer">{t('nav.volunteer')}</Link>}
              {visibility.donate !== false && <Link to="/donate" className="text-white/70 hover:text-gold transition-colors text-sm" data-testid="footer-donate">{t('nav.donate')}</Link>}
              {visibility.prayers !== false && <Link to="/prayers" className="text-white/70 hover:text-gold transition-colors text-sm" data-testid="footer-prayers">{t('nav.prayers')}</Link>}
              {visibility.programs !== false && <Link to="/programs" className="text-white/70 hover:text-gold transition-colors text-sm" data-testid="footer-programs">{t('nav.programs')}</Link>}
              {visibility.pantries !== false && <Link to="/pantries" className="text-white/70 hover:text-gold transition-colors text-sm" data-testid="footer-pantries">{t('nav.pantries')}</Link>}
              <Link to="/resources" className="text-white/70 hover:text-gold transition-colors text-sm" data-testid="footer-resources">Community Resources</Link>
              <Link to="/local-businesses" className="text-white/70 hover:text-gold transition-colors text-sm" data-testid="footer-businesses">Local Businesses</Link>
              <Link to="/business-teaching" className="text-white/70 hover:text-gold transition-colors text-sm" data-testid="footer-business-teaching">Business Classes</Link>
              {visibility.contact !== false && <Link to="/contact" className="text-white/70 hover:text-gold transition-colors text-sm" data-testid="footer-contact">{t('nav.contact')}</Link>}
            </div>
          </div>
          
          {/* Contact Info */}
          <div>
            <h4 className="font-serif text-lg text-gold mb-4">{t('footer.contactUs')}</h4>
            <div className="flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <MapPin size={16} className="text-gold mt-1 flex-shrink-0" />
                <span className="text-white/70 text-sm">
                  109 N Beaumont Avenue #1078<br />
                  Kissimmee, FL 34741
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={16} className="text-gold flex-shrink-0" />
                <span className="text-white/70 text-sm">(407) 216-2853</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={16} className="text-gold flex-shrink-0" />
                <span className="text-white/70 text-sm">livingfruitministry@gmail.com</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/50 text-sm">
            © {new Date().getFullYear()} {t('footer.copyright')}
          </p>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <Link 
              to="/privacy" 
              className="text-white/50 text-sm hover:text-gold transition-colors"
              data-testid="footer-privacy-link"
            >
              Privacy Policy
            </Link>
            <span className="text-white/30">|</span>
            <Link 
              to="/terms" 
              className="text-white/50 text-sm hover:text-gold transition-colors"
              data-testid="footer-terms-link"
            >
              Terms of Service
            </Link>
            <span className="text-white/30">|</span>
            <Link 
              to="/transparency" 
              className="text-white/50 text-sm hover:text-gold transition-colors"
              data-testid="footer-transparency-link"
            >
              Financial Transparency
            </Link>
          </div>
          <p className="text-white/50 text-sm">
            {t('footer.nonprofit')}
          </p>
        </div>
      </div>
    </footer>
  );
}
